import { KeyboardEvent, useId, useState } from "react";
import { Chip, TextField } from "@mui/material";

import "./style.scss";

interface Props {
    label: string;
    values: string[];
    onChange: (values: string[]) => void;
    /** Returns the canonical form of an entry, or "" when it is not valid. */
    normalize: (raw: string) => string;
    placeholder?: string;
    invalidMessage: string;
    hint?: string;
    disabled?: boolean;
}

/**
 * A list of short strings (domains, ids) edited as chips. Entries are added
 * with Enter, comma, or on blur, after `normalize`; duplicates are ignored.
 */
export const StringListInput = ({
    label,
    values,
    onChange,
    normalize,
    placeholder,
    invalidMessage,
    hint,
    disabled
}: Props) => {
    const [draft, setDraft] = useState("");
    const [error, setError] = useState("");
    const id = useId();

    const commit = () => {
        const parts = draft
            .split(/[,\s]+/)
            .map(p => p.trim())
            .filter(Boolean);
        if (!parts.length) return;
        const next = [...values];
        const bad: string[] = [];
        for (const part of parts) {
            const n = normalize(part);
            if (!n) bad.push(part);
            else if (!next.includes(n)) next.push(n);
        }
        if (bad.length) {
            setError(`${invalidMessage}: ${bad.join(", ")}`);
            setDraft(bad.join(", "));
        } else {
            setError("");
            setDraft("");
        }
        if (next.length !== values.length) onChange(next);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
        } else if (e.key === "Backspace" && !draft && values.length) {
            onChange(values.slice(0, -1));
        }
    };

    return (
        <div className="string-list-input">
            <TextField
                id={id}
                label={label}
                size="small"
                fullWidth
                value={draft}
                placeholder={placeholder}
                disabled={disabled}
                error={!!error}
                helperText={error || hint}
                onChange={e => {
                    setDraft(e.target.value);
                    if (error) setError("");
                }}
                onKeyDown={handleKeyDown}
                onBlur={commit}
                InputProps={{
                    startAdornment: values.length ? (
                        <div className="string-list-input__chips">
                            {values.map(v => (
                                <Chip
                                    key={v}
                                    label={v}
                                    size="small"
                                    disabled={disabled}
                                    onDelete={() => onChange(values.filter(x => x !== v))}
                                />
                            ))}
                        </div>
                    ) : undefined
                }}
            />
        </div>
    );
};
